/* eslint-disable react/require-default-props */
import { Trans, t } from '@lingui/macro';
import { StandardTagger, ListboxOption } from '@brandfolder/react';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';

import { getBrandfolderFaces } from '@api/v4/private/people_tagging/faces';
import { Person } from '@components/people_tags/customPeopleTagTypes';
import { useCroppedImage } from '@components/bulk_management/people_management/hooks/useCroppedImage';

interface PeopleFilterProps {
  filters: {
    people?: string[];
  };
  updateFilters: (key: string, value: any) => void;
}

const PeopleFilterWrapper = styled.div`
  padding: 20px 25px;

  textarea.bf-combobox__combobox {
    width: calc(100% - 10px);
  }
`;

const FaceImage = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
`;

const CroppedFaceImageComponent: React.FunctionComponent<{
  src?: string;
  alt: string;
  boundingBox?: { left: number; top: number; width: number; height: number };
}> = ({ src, alt, boundingBox }) => {
  const { croppedImageUrl } = useCroppedImage(src, boundingBox);

  if (!src) {
    return null;
  }

  return <FaceImage src={croppedImageUrl || src} alt={alt} />;
};

const PeopleFilter: React.FunctionComponent<PeopleFilterProps> = ({
  filters,
  updateFilters,
}) => {
  const [availablePeople, setAvailablePeople] = useState<Person[]>([]);
  const [selectedPeopleIds, setSelectedPeopleIds] = useState<Set<string>>(
    new Set(filters.people || [])
  );
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    const fetchPeopleData = async () => {
      try {
        const brandfolderKey = BFG.brandfolder_key;
        if (!brandfolderKey) {
          return;
        }

        const response = await getBrandfolderFaces({ brandfolderKey });

        // Transform API response to Person type
        const people: Person[] = response
          .filter((face) => face.identity_name && face.identity_name.trim() !== '')
          .map((face) => ({
            person_id: face.id,
            tag_name: face.identity_name,
            face_uri: face.source_url,
            bounding_box: face.bounding_box,
          }));

        setAvailablePeople(people);

      } catch (error) {
        console.error('Error fetching people data:', error);
      }
    };

    fetchPeopleData();
  }, []);

  const handleTaggedChange = (tags: ListboxOption[], action?: string) => {
    try {
      const selectedIds = tags
        .filter((tag) => tag && tag.value != null)
        .map((tag) => {
          const value = tag.value;
          return typeof value === 'string' ? value : String(value);
        });
      setSelectedPeopleIds(new Set(selectedIds));
      updateFilters('people', selectedIds);
    } catch (error) {
      console.error('Error in handleTaggedChange:', error);
    }
  };

  const taggerOptions: ListboxOption[] = useMemo(() => {
    return availablePeople.map((person) => ({
      key: person.person_id,
      label: person.tag_name,
      value: person.person_id,
      children: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CroppedFaceImageComponent
            src={person.face_uri}
            alt={person.tag_name}
            boundingBox={person.bounding_box}
          />
          <span>{person.tag_name}</span>
        </div>
      ),
    } as ListboxOption));
  }, [availablePeople]);

  const filteredOptions: ListboxOption[] = useMemo(() => {
    if (!searchValue.trim()) {
      return taggerOptions;
    }
    const query = searchValue.toLowerCase();
    return taggerOptions.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [taggerOptions, searchValue]);

  const initialTags: ListboxOption[] = useMemo(() => {
    return Array.from(selectedPeopleIds)
      .map((id) => {
        const person = availablePeople.find((p) => p.person_id === id);
        if (person) {
          return {
            key: person.person_id,
            label: person.tag_name,
            value: person.person_id,
            children: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CroppedFaceImageComponent
                  src={person.face_uri}
                  alt={person.tag_name}
                  boundingBox={person.bounding_box}
                />
                <span>{person.tag_name}</span>
              </div>
            ),
          } as ListboxOption;
        }
        return null;
      })
      .filter(Boolean) as ListboxOption[];
  }, [selectedPeopleIds, availablePeople]);

  if (availablePeople.length === 0) {
    return null;
  }

  return (
    <PeopleFilterWrapper>
      <StandardTagger
        id="people-filter-tagger"
        initialTags={initialTags}
        labels={{
          label: t`People`,
          emptyLabel: t`No people found`,
          listboxLabel: t`List of people`,
          removeTagLabel: t`Remove person`,
          iconButtonLabel: t`Toggle list of people`,
        }}
        onTagged={(tags) => handleTaggedChange(tags)}
        options={filteredOptions}
        allowCustomValue={false}
        allowDuplicates={false}
        showLabel={true}
        search={false}
        comboboxProps={{
          comboboxTextareaProps: {
            onChange: (e) => {
              setSearchValue(e.target.value);
            },
            placeholder: t`Search people...`,
          }
        }}
      />
    </PeopleFilterWrapper>
  );
};

PeopleFilter.propTypes = {
  filters: PropTypes.shape({
    people: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  updateFilters: PropTypes.func.isRequired,
};

export default PeopleFilter;
